"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import Footer from "@/components/marketing/footer";

export default function InHousePage() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const show = (id: string) => visibleSections.has(id);

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "'Georgia', 'Times New Roman', serif", scrollBehavior: "smooth" }}>
      <style>{`html { scroll-behavior: smooth; }`}</style>

      {/* ─── NAV ─── */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 40 ? "bg-white shadow-md py-1" : "bg-white/90 py-1.5"
      }`}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <Link href="/" className="flex items-center cursor-pointer">
            <img src="/logo.png" alt="Contract Lucidity" className="h-12 md:h-14" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link href="/features" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Features</Link>
            <Link href="/how-it-works" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>How It Works</Link>
            <Link href="/law-firms" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Law Firms</Link>
            <Link href="/in-house" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>In-House</Link>
            <Link href="/security" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Security</Link>
            <Link href="/why-contract-lucidity" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Why Us</Link>
            <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#C8A84B] hover:text-[#b8993f] transition-colors font-semibold" style={{ fontFamily: "system-ui, sans-serif" }}>Lucid Vault</a>
            <Link
              href="/login"
              className="px-6 py-2.5 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-[#2E5EA0] transition-colors"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Sign In
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2" aria-label="Toggle menu">
            <svg className="w-6 h-6 text-[#1B3A6B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" style={{ fontFamily: "system-ui, sans-serif" }}>
            <div className="flex flex-col px-6 py-4 gap-3">
              <Link href="/features" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Features</Link>
              <Link href="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">How It Works</Link>
              <Link href="/law-firms" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Law Firms</Link>
              <Link href="/in-house" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">In-House</Link>
              <Link href="/security" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Security</Link>
              <Link href="/why-contract-lucidity" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Why Us</Link>
              <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="text-sm text-[#C8A84B] font-semibold hover:text-[#b8993f] py-2 border-b border-gray-50">Lucid Vault</a>
              <Link href="/login" onClick={() => setMobileMenuOpen(false)} className="text-sm font-semibold text-white bg-[#1B3A6B] rounded px-4 py-2.5 text-center mt-2">Sign In</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative pt-20 min-h-[520px] flex items-center">
        <div className="absolute inset-0">
          <img src="/images/in-house-hero.webp" alt="In-house legal team" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-[#1B3A6B]/80" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 py-24 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl leading-[1.15] mb-6 text-white">
            Your legal department is a bottleneck.<br />
            <span className="text-[#C8A84B]">It doesn&apos;t have to be.</span>
          </h1>
          <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
            Contract Lucidity gives in-house teams the tools to review faster, track obligations, and maintain portfolio visibility.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="#cta"
              className="px-8 py-4 text-sm font-semibold bg-[#C8A84B] text-white rounded hover:bg-[#b8982e] transition-colors"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Request a Demo
            </a>
            <a
              href="#pain-points"
              className="px-8 py-4 text-sm font-semibold text-white border border-white/30 rounded hover:border-white transition-colors"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* ─── PAIN POINTS ─── */}
      <section id="pain-points" data-animate className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-3 gap-12 transition-all duration-700 ${show("pain-points") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {[
              {
                stat: "3-5 days",
                label: "average contract review turnaround",
                delay: "0ms",
              },
              {
                stat: "40-60",
                label: "clauses per commercial agreement",
                delay: "150ms",
              },
              {
                stat: "$0",
                label: "visibility into obligation compliance across your portfolio",
                delay: "300ms",
              },
            ].map((card, i) => (
              <div
                key={i}
                className={`text-center transition-all duration-700 ${show("pain-points") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
                style={{ transitionDelay: card.delay }}
              >
                <p className="text-5xl font-bold text-[#1B3A6B] mb-2">{card.stat}</p>
                <p className="text-sm font-semibold text-[#C8A84B] uppercase tracking-wide" style={{ fontFamily: "system-ui, sans-serif" }}>{card.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── VALUE PROPOSITIONS ─── */}
      <section id="value-props" data-animate className="py-24 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-700 ${show("value-props") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Built for In-House Teams</p>
            <h2 className="text-3xl md:text-4xl text-[#1B3A6B]">
              The tools your legal department<br />actually needs.
            </h2>
          </div>

          {/* VP 1 — Portfolio-Wide Obligation Tracking */}
          <div
            id="vp-obligations"
            data-animate
            className={`mb-20 transition-all duration-700 ${show("vp-obligations") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100">
                <img src="/images/in-house-gc.webp" alt="General counsel reviewing obligation dashboard" className="w-full h-64 object-cover" loading="lazy" />
              </div>
              <div>
                <h3 className="text-2xl text-[#1B3A6B] mb-4">Portfolio-Wide Obligation Tracking</h3>
                <div className="space-y-4 text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                  <p>Every contract creates obligations. Payment deadlines, renewal windows, compliance requirements, insurance certificates.</p>
                  <p>Contract Lucidity extracts them automatically, calculates due dates from priority, and exports directly to your team&apos;s calendar.</p>
                  <p>CSV export feeds your existing tracking systems. Custom obligations for items the AI didn&apos;t catch.</p>
                  <p className="font-semibold text-[#1B3A6B]">One dashboard showing obligations across all contracts and projects.</p>
                </div>
              </div>
            </div>
          </div>

          {/* VP 2 — Cross-Document Intelligence */}
          <div
            id="vp-crossdoc"
            data-animate
            className={`mb-20 transition-all duration-700 ${show("vp-crossdoc") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <h3 className="text-2xl text-[#1B3A6B] mb-4">Cross-Document Intelligence</h3>
                <div className="space-y-4 text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                  <p>Your MSA says one thing. Your SOW says another. Your NDA has different confidentiality terms than both.</p>
                  <p>Upload related contracts together. AI identifies conflicts, coverage gaps, and alignment issues that no one has time to cross-reference manually.</p>
                  <p className="font-semibold text-[#1B3A6B]">Stop discovering conflicts during disputes. Find them during review.</p>
                </div>
              </div>
              <div className="order-1 md:order-2 rounded-xl overflow-hidden shadow-lg border border-gray-100">
                <img src="/images/in-house-compliance.webp" alt="Cross-document analysis showing conflicts" className="w-full h-64 object-cover" loading="lazy" />
              </div>
            </div>
          </div>

          {/* VP 3 — Version Control for Negotiations */}
          <div
            id="vp-versions"
            data-animate
            className={`mb-20 transition-all duration-700 ${show("vp-versions") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="bg-white rounded-lg p-10 border border-gray-100 shadow-sm">
              <h3 className="text-2xl text-[#1B3A6B] mb-4">Version Control for Negotiations</h3>
              <div className="space-y-4 text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                <p>Upload the original. Upload the revision. See exactly what changed with a full-document redline.</p>
                <p>The &ldquo;Show Changes&rdquo; toggle highlights what&apos;s different across every tab &mdash; clause analysis, obligations, executive summary.</p>
                <p>Download any version. Export the redline as DOCX. Everything you need for your document management system.</p>
              </div>
            </div>
          </div>

          {/* VP 4 — Enterprise Ready */}
          <div
            id="vp-enterprise"
            data-animate
            className={`transition-all duration-700 ${show("vp-enterprise") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="bg-[#1B3A6B] rounded-lg p-10 text-center">
              <h3 className="text-2xl text-white mb-6">Enterprise Ready</h3>
              <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                {[
                  "Self-hosted Docker deployment — your data stays on your infrastructure",
                  "Multi-AI provider support (Anthropic Claude + OpenAI GPT)",
                  "Enterprise identity integration with OIDC SSO, SCIM 2.0, and REST API (OAuth 2.0)",
                  "Group-based project access for organizing legal team workflows",
                  "File integrity validation on every upload",
                  "No vendor lock-in",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 text-left">
                    <span className="text-[#C8A84B] mt-0.5 shrink-0">&#10003;</span>
                    <p className="text-sm text-white/70 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── LUCID VAULT CALLOUT ─── */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-[#F8F9FB] rounded-xl p-8 md:p-10 border border-gray-100 flex flex-col md:flex-row items-center gap-8">
            <div className="shrink-0">
              <span className="w-14 h-14 rounded-full bg-[#C8A84B] text-white flex items-center justify-center text-lg font-bold" style={{ fontFamily: "system-ui, sans-serif" }}>LV</span>
            </div>
            <div className="flex-1">
              <h3 className="text-xl text-[#1B3A6B] mb-2">Securely exchange contracts with vendors and counterparties.</h3>
              <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                Lucid Vault provides your legal department a branded, encrypted portal for document exchange. Vendors and counterparties upload through your domain — no more contracts buried in email threads.
              </p>
            </div>
            <a
              href="https://lucidvault.contractlucidity.com"
              rel="noopener noreferrer"
              className="shrink-0 px-6 py-3 text-sm font-semibold bg-[#C8A84B] text-white rounded hover:bg-[#b8993f] transition-colors whitespace-nowrap"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Explore Lucid Vault
            </a>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section id="cta" data-animate className="py-24 bg-[#C8A84B]">
        <div className="max-w-3xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("cta") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-white rounded-xl border-2 border-[#1B3A6B] p-12 text-center shadow-lg">
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">
                Stop being the bottleneck.
              </h2>
              <p className="text-gray-500 mb-10 max-w-xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                See how Contract Lucidity helps in-house teams review faster, track every obligation, and maintain portfolio-wide visibility.
              </p>
              <a
                href="/demo"
                className="inline-block px-10 py-4 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-white hover:text-[#1B3A6B] hover:ring-2 hover:ring-[#1B3A6B] transition-all"
                style={{ fontFamily: "system-ui, sans-serif" }}
              >
                Request a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
